import { useEffect, useRef, useState } from "react";

export interface ViewportDimensions {
  height: number;
  width: number;
  vh: string; // px
  vw: string; // px
}

export function useViewportHeight(): ViewportDimensions {
  const [dimensions, setDimensions] = useState<ViewportDimensions>(() => {
    const h = typeof window !== "undefined" ? window.innerHeight : 0;
    const w = typeof window !== "undefined" ? window.innerWidth : 0;
    return { height: h, width: w, vh: `${h}px`, vw: `${w}px` };
  });

  const rafId = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const apply = (w: number, h: number) => {
      // 1% Units als px speichern (für CSS calc(...))
      document.documentElement.style.setProperty("--vh", `${h * 0.01}px`);
      document.documentElement.style.setProperty("--vw", `${w * 0.01}px`);
      setDimensions({ height: h, width: w, vh: `${h}px`, vw: `${w}px` });
    };

    const measureFromWindow = () => apply(window.innerWidth, window.innerHeight);

    const scheduleApply = (w: number, h: number) => {
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => apply(w, h));
    };

    const vv = window.visualViewport;

    // Handler (als Referenzen behalten, damit Cleanup funktioniert)
    const onVVChange = () => {
      // visualViewport gibt „sichtbaren“ Bereich (Keyboard/URL-Bar berücksichtigt)
      // width/height sind in CSS-Pixeln
      if (!vv) return;
      scheduleApply(Math.round(vv.width), Math.round(vv.height));
    };

    const onWindowResize = () => {
      // Fallback wenn kein visualViewport vorhanden
      if (vv) return; // wenn vv existiert, übernehmen seine Events
      scheduleApply(window.innerWidth, window.innerHeight);
    };

    const onVisibility = () => {
      // Nach Tab-Wechsel oder PWA-Resume einmal hart messen
      setTimeout(() => {
        if (vv) onVVChange();
        else measureFromWindow();
      }, 0);
    };

    // Initial messen
    if (vv) {
      onVVChange();
      vv.addEventListener("resize", onVVChange);
      vv.addEventListener("scroll", onVVChange); // URL-/Toolbars, Keyboard
    } else {
      measureFromWindow();
      window.addEventListener("resize", onWindowResize, { passive: true });
    }

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onVisibility); // bfcache / iOS Resume

    return () => {
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onVisibility);

      if (vv) {
        vv.removeEventListener("resize", onVVChange);
        vv.removeEventListener("scroll", onVVChange);
      } else {
        window.removeEventListener("resize", onWindowResize);
      }
    };
  }, []);

  return dimensions;
}

export function useViewportHeightSimple(): string {
  const [vh, setVh] = useState<string>(() =>
    typeof window !== "undefined" ? `${window.innerHeight}px` : "100vh"
  );

  const rafId = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const apply = (h: number, w: number) => {
      document.documentElement.style.setProperty("--vh", `${h * 0.01}px`);
      document.documentElement.style.setProperty("--vw", `${w * 0.01}px`);
      setVh(`${h}px`);
    };

    const schedule = (h: number, w: number) => {
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => apply(h, w));
    };

    const vv = window.visualViewport;

    const onVV = () => vv && schedule(Math.round(vv.height), Math.round(vv.width));
    const onWin = () => schedule(window.innerHeight, window.innerWidth);
    const onVisibility = () => setTimeout(() => (vv ? onVV() : onWin()), 0);

    if (vv) {
      onVV();
      vv.addEventListener("resize", onVV);
      vv.addEventListener("scroll", onVV);
    } else {
      onWin();
      window.addEventListener("resize", onWin, { passive: true });
    }

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onVisibility);

    return () => {
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onVisibility);
      if (vv) {
        vv.removeEventListener("resize", onVV);
        vv.removeEventListener("scroll", onVV);
      } else {
        window.removeEventListener("resize", onWin);
      }
    };
  }, []);

  return vh;
}
